import { $mediaStatus } from '../stores/deviceStatus';
import { $scroll } from '../stores/scroll';

interface InviewEventDetail {
    way: 'enter' | 'exit';
}

export class Sun extends HTMLElement {
    static EVENTS = {
        INVIEW: 'sun-inview'
    };
    private isInview: boolean;
    private $svg: HTMLElement;
    private rotate: number;
    private rafId: number | null;
    private easedVelocity: number;

    constructor() {
        super();

        // Flags
        this.isInview = false;
        this.rotate = 0;
        this.easedVelocity = 0;

        // UI
        this.$svg = this.querySelector('[data-svg]') as HTMLElement;
    }

    ///////////////
    // Lifecycle
    ///////////////
    connectedCallback() {
        if ($mediaStatus.get().isReducedMotion) return;

        this.bindEvents();
    }

    disconnectedCallback() {
        this.unbindEvents();
    }

    ///////////////
    // Events
    ///////////////
    bindEvents() {
        window.addEventListener(`${Sun.EVENTS.INVIEW}-${this.id}`, this.onInview as EventListener);
    }

    unbindEvents() {
        window.removeEventListener(
            `${Sun.EVENTS.INVIEW}-${this.id}`,
            this.onInview as EventListener
        );
    }

    private onInview = (e: CustomEvent<InviewEventDetail>): void => {
        if (e.detail.way === 'enter' && !$mediaStatus.get().isReducedMotion) {
            this.isInview = true;
            this.play();
        } else {
            this.isInview = false;
            this.stop();
        }
    };

    private play = () => {
        this.anim();
        this.rafId = requestAnimationFrame(this.play);
    };

    private stop = () => {
        if (this.rafId) {
            cancelAnimationFrame(this.rafId);
            this.rafId = null;
        }
    };

    private easeOutQuint = (x: number): number => {
        return 1 - Math.pow(1 - x, 5);
    };

    private easeVelocity = (targetVelocity: number, currentVelocity: number): number => {
        // Normalize the velocity difference to 0-1 range for easing
        const maxVelocity = 10; // Adjust this based on your typical max velocity
        const velocityDiff = Math.abs(targetVelocity - currentVelocity);
        const normalizedDiff = Math.min(velocityDiff / maxVelocity, 1);

        // Apply easeOut to the difference
        const easedDiff = this.easeOutQuint(normalizedDiff);

        // Apply the eased difference
        if (targetVelocity > currentVelocity) {
            return currentVelocity + velocityDiff * easedDiff;
        } else {
            return currentVelocity - velocityDiff * easedDiff;
        }
    };

    private anim = () => {
        const { velocity, direction } = $scroll.get();

        this.easedVelocity = this.easeVelocity(velocity, this.easedVelocity);

        // Rotation based on direction and velocity
        const rotationAmount = direction * (0.1 + Math.abs(this.easedVelocity) * 0.035);

        this.rotate += rotationAmount;
        this.$svg.style.transform = `rotate(${this.rotate}deg)`;
    };
}
